import { useState, useEffect } from 'react'
import { X } from 'lucide-react'
import { Select } from '../ui/Select'
import { useAuth } from '../../contexts/AuthContext'
import { useUserCheck } from '../../hooks/api/useUserCheck'
import { useAllCompanies } from '../../hooks/api/useCompanies'
import { supabase } from '../../lib/supabase'

interface AddKaseyaGroupModalProps {
  isOpen: boolean
  onClose: () => void
  onGroupAdded?: () => void
  preselectedCompanyId?: string | null
}

export const AddKaseyaGroupModal = ({
  isOpen,
  onClose,
  onGroupAdded,
  preselectedCompanyId
}: AddKaseyaGroupModalProps) => {
  const { user } = useAuth()
  const { data: userRecord } = useUserCheck(user?.id || '')
  const isFullAdmin = userRecord?.admin_level === 'full'

  const [formData, setFormData] = useState({
    companyId: preselectedCompanyId || userRecord?.company_id || '',
    kaseyaOrganizationId: '',
    kaseyaGroupId: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { data: companies = [] } = useAllCompanies()

  // Update companyId when preselectedCompanyId changes
  useEffect(() => {
    if (preselectedCompanyId) {
      setFormData(prev => ({ ...prev, companyId: preselectedCompanyId }))
    } else if (!isFullAdmin && userRecord?.company_id) {
      setFormData(prev => ({ ...prev, companyId: userRecord.company_id }))
    }
  }, [preselectedCompanyId, isFullAdmin, userRecord?.company_id])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!formData.companyId) {
      setError('Please select an organization')
      return
    }

    if (!formData.kaseyaOrganizationId.trim()) {
      setError('Please enter a Kaseya Organization ID')
      return
    }

    if (!formData.kaseyaGroupId.trim()) {
      setError('Please enter a Kaseya Group ID')
      return
    }

    setIsSubmitting(true)

    try {
      const kaseyaOrgIdNum = parseInt(formData.kaseyaOrganizationId)
      const kaseyaGroupIdNum = parseInt(formData.kaseyaGroupId)

      if (isNaN(kaseyaOrgIdNum)) {
        setError('Kaseya Organization ID must be a number')
        setIsSubmitting(false)
        return
      }

      if (isNaN(kaseyaGroupIdNum)) {
        setError('Kaseya Group ID must be a number')
        setIsSubmitting(false)
        return
      }

      // Update company with all Kaseya IDs
      const { error: updateError } = await supabase
        .from('companies')
        .update({
          kaseya_organization_id: kaseyaOrgIdNum,
          kaseya_group_id: kaseyaGroupIdNum,
          default_kaseya_group_id: kaseyaGroupIdNum
        })
        .eq('id', formData.companyId)

      if (updateError) {
        console.error('Failed to update company:', updateError)
        setError('Failed to add Kaseya group. Please try again.')
        setIsSubmitting(false)
        return
      }

      // Reset form
      setFormData({
        companyId: preselectedCompanyId || userRecord?.company_id || '',
        kaseyaOrganizationId: '',
        kaseyaGroupId: ''
      })

      // Notify parent and close
      onGroupAdded?.()
      onClose()
    } catch (err: any) {
      console.error('Error adding Kaseya group:', err)
      setError('An unexpected error occurred')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    setError(null)
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 pb-0">
          <h2 className="text-xl font-medium text-gray-900">Add Kaseya Group</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Organization (Full Admins Only) */}
          {isFullAdmin && !preselectedCompanyId && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Organization <span className="text-red-500">*</span>
              </label>
              <Select
                value={formData.companyId}
                onChange={(e) => handleInputChange('companyId', e.target.value)}
                className="w-full"
                required
              >
                <option value="">Select Organization</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </Select>
            </div>
          )}

          {/* Kaseya Organization ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Kaseya Organization ID <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.kaseyaOrganizationId}
              onChange={(e) => handleInputChange('kaseyaOrganizationId', e.target.value)}
              placeholder="e.g., 152"
              className="w-full px-3 py-2 border border-gray-300 text-sm rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              The Organization ID from Kaseya VSA
            </p>
          </div>

          {/* Kaseya Group ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Kaseya Group ID (Machine Group) <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.kaseyaGroupId}
              onChange={(e) => handleInputChange('kaseyaGroupId', e.target.value)}
              placeholder="e.g., 108"
              className="w-full px-3 py-2 border border-gray-300 text-sm rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              The Machine Group ID from Kaseya VSA
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-600">
              {error}
            </div>
          )}

          {/* Info Box */}
          <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-xs text-blue-700">
            <p className="font-medium mb-1">Note:</p>
            <p>
              This will configure the Kaseya VSA integration for this organization.
              Both Organization ID and Machine Group ID can be found in your Kaseya VSA management console.
            </p>
          </div>

          {/* Submit Button */}
          <div className="pt-2">
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gray-900 text-white py-2 px-4 rounded-full text-sm hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? 'Adding Group...' : 'Add Group'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
